import { CalendarEventCard } from "@/components/event/CalendarEventCard.tsx";
import { EventPreviewModal } from "@/components/event/EventPreviewModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import {
  ApiEvent,
  ApiEventAttendeeResponseStatuses,
  CalendarEventType,
} from "@/types/event.types.ts";
import { ApiTimeOffLogFullPreview } from "@/types/time-off/time-off.types.ts";
import { EventContentArg } from "@fullcalendar/core";
import { Avatar, toCssColorValue, UIKitColors, useModals } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface EventInCalendarProps {
  eventInfo: EventContentArg;
  viewAsEmployeeId?: number;
  timezone: string;
}

export function EventInCalendar({ eventInfo, viewAsEmployeeId, timezone }: EventInCalendarProps) {
  const modalsService = useModals();
  const { t } = useTranslation();
  const { renderTime } = useFormatter();
  const { openViewRequest } = useTimeOffHook();

  const eventType = useMemo(() => {
    return eventInfo.event.extendedProps?.eventType as CalendarEventType | undefined;
  }, [eventInfo]);

  const eventData = useMemo(() => {
    return eventInfo.event.extendedProps?.data;
  }, [eventInfo]);

  const eventEmoji = useMemo(() => {
    return eventInfo.event.extendedProps?.emoji as string | undefined;
  }, [eventInfo]);

  const myEvent = useMemo(() => {
    if (eventType === CalendarEventType.event && eventData) {
      return eventData as ApiEvent;
    }
    return undefined;
  }, [eventType, eventData]);

  const durationMinutes =
    eventInfo.event.start && eventInfo.event.end
      ? (eventInfo.event.end.getTime() - eventInfo.event.start.getTime()) / 60_000
      : 30;
  const time =
    !eventInfo.event.allDay && eventInfo.event.start
      ? durationMinutes >= 60 && eventInfo.event.end
        ? `${renderTime(eventInfo.event.start, timezone)} — ${renderTime(eventInfo.event.end, timezone)}`
        : renderTime(eventInfo.event.start, timezone)
      : undefined;

  const handleClick = () => {
    if (eventType === CalendarEventType.event && myEvent) {
      modalsService.openModal(EventPreviewModal, {
        event: myEvent,
      });
    } else if (eventType === CalendarEventType.time_off && eventData) {
      const timeOffRequest = eventData as ApiTimeOffLogFullPreview;
      if (timeOffRequest.acl?.can_view) {
        openViewRequest(timeOffRequest.id);
      }
    }
  };

  const viewerAsAttendee = useMemo(() => {
    if (!viewAsEmployeeId || !myEvent?.attendees) return undefined;
    return myEvent.attendees.find((attendee) => attendee.employee?.id === viewAsEmployeeId);
  }, [viewAsEmployeeId, myEvent]);

  const eventClasses = useMemo(() => {
    const classes: string[] = [];
    if (viewerAsAttendee) {
      switch (viewerAsAttendee.response_status) {
        case ApiEventAttendeeResponseStatuses.accepted:
          classes.push("bg-brand-dark/80 text-light");
          break;
        case ApiEventAttendeeResponseStatuses.declined:
          classes.push("bg-brand-dark/30 text-light line-through border");
          break;
        case ApiEventAttendeeResponseStatuses.tentative:
          classes.push("text-light  bg-brand-dark/60 bg-[length:12px_12px] ");
          break;
        case ApiEventAttendeeResponseStatuses.needs_action:
          classes.push("border-brand-dark border text-brand-dark bg-brand-light");
          break;
      }
    } else {
      classes.push("bg-brand-dark text-light");
    }
    return classes;
  }, [viewerAsAttendee, myEvent]);

  const eventStyles = useMemo(() => {
    if (viewerAsAttendee?.response_status === ApiEventAttendeeResponseStatuses.tentative) {
      return {
        background:
          "linear-gradient(135deg, hsl(var(--var-color-brand-dark) / 0.8) 5.78%, hsl(var(--var-color-brand-dark) / 0.8) 11.11%, hsl(var(--var-color-brand-dark) / 0.6) 11.11%, hsl(var(--var-color-brand-dark) /  0.6) 16.44%, hsl(var(--var-color-brand-dark) /  0.8) 16.44%, hsl(var(--var-color-brand-dark) /  0.8) 21.78%, hsl(var(--var-color-brand-dark) /  0.6) 21.78%, hsl(var(--var-color-brand-dark) /  0.6) 27.11%, hsl(var(--var-color-brand-dark) /  0.8) 27.11%, hsl(var(--var-color-brand-dark) /  0.8) 32.44%, hsl(var(--var-color-brand-dark) /  0.6) 32.44%, hsl(var(--var-color-brand-dark) /  0.6) 37.78%, hsl(var(--var-color-brand-dark) /  0.8) 37.78%, hsl(var(--var-color-brand-dark) /  0.8) 43.11%, hsl(var(--var-color-brand-dark) /  0.6) 43.11%, hsl(var(--var-color-brand-dark) /  0.6) 48.44%, hsl(var(--var-color-brand-dark) /  0.8) 48.44%, hsl(var(--var-color-brand-dark) /  0.8) 53.78%, hsl(var(--var-color-brand-dark) /  0.6) 53.78%, hsl(var(--var-color-brand-dark) /  0.6) 59.11%)",
      };
    }

    if (eventType === CalendarEventType.time_off && eventData) {
      const timeOffRequest = eventData as ApiTimeOffLogFullPreview;
      const categoryColor = timeOffRequest.time_off_data?.category?.color;
      return {
        backgroundColor: toCssColorValue(categoryColor, UIKitColors.dark),
      };
    }

    return {};
  }, [viewerAsAttendee, eventType, eventData]);

  return (
    <CalendarEventCard
      durationMinutes={durationMinutes}
      onClick={handleClick}
      appearanceClassName={
        eventType !== CalendarEventType.time_off ? twMerge(eventClasses) : undefined
      }
      style={eventStyles}
      withMarker={eventType !== CalendarEventType.time_off}
      time={time}
      title={
        <>
          {myEvent?.interview?.job && (
            <Avatar emoji={myEvent.interview.job.emoji} size="sm" className="-ml-0.5 mr-1" />
          )}
          {eventEmoji && <Avatar emoji={eventEmoji} size="sm" className="-ml-1 mr-0.5" />}
          <span className="whitespace-nowrap text-ellipsis overflow-hidden inline">
            {eventInfo.event.title || `(${t("calendar:no_title")})`}
          </span>
        </>
      }
    />
  );
}
